import type { ErpSparePart } from '~/types/part'
import type { ShareCartItemHydrated } from '~/types/item'

/** Every spare part row uses the same placeholder image, never a stored URL. */
export const SPARE_PART_IMAGE = '/images/icons/varaosat.webp'

/**
 * A spare part SKU is stored as productCode@supplierNumber. Everything after
 * the first '@' is the supplier number.
 */
export const splitSparePartSku = (sku: string): { productCode: string, supplierCode: string } => {
  if (typeof sku !== 'string' || !sku.includes('@')) {
    return { productCode: sku || '', supplierCode: '' }
  }
  const separator = sku.indexOf('@')
  return { productCode: sku.slice(0, separator), supplierCode: sku.slice(separator + 1) }
}

/**
 * The link for a spare part row, derived from the SKU rather than stored.
 * /varaosa/[slug] accepts the productCode@supplierNumber form, so this resolves
 * to the canonical page for exactly that part.
 */
export const buildSparePartLink = (sku: string): string => {
  const { productCode } = splitSparePartSku(sku)
  return productCode ? `/varaosa/${encodeURIComponent(sku)}` : '/varaosahaku'
}

/**
 * What a shared cart spare part row shows, and whether it may be bought.
 *
 * `verified` is true only when the ERP confirmed the price. Spare part prices
 * are not revalidated server side when the order is created (paymentGateway
 * skips the Algolia validation for them), so an unverified price must never
 * reach the cart - the row stays visible on the spare part database's list
 * price instead, which is our own data rather than the caller's.
 */
export const getSparePartRowPrice = (
  item: Pick<ShareCartItemHydrated, 'sparePartData' | 'sparePartPriceVerified'>
): { finalPrice: number, nonDiscountedPrice: number, verified: boolean } => {
  const data: ErpSparePart | null | undefined = item.sparePartData
  const finalPrice = typeof data?.finalPrice === 'number' && Number.isFinite(data.finalPrice) ? data.finalPrice : 0
  const price = typeof data?.price === 'number' && Number.isFinite(data.price) ? data.price : finalPrice

  return {
    finalPrice,
    nonDiscountedPrice: price,
    verified: item.sparePartPriceVerified === true && finalPrice > 0
  }
}

/**
 * The price a shared cart spare part may be added to the cart at, or null when
 * there is none.
 */
export const getVerifiedSparePartPrice = (
  item: Pick<ShareCartItemHydrated, 'sparePartData' | 'sparePartPriceVerified'>
): number | null => {
  const row = getSparePartRowPrice(item)
  return row.verified ? row.finalPrice : null
}
